// The promo bar stack: the storefront coupon bar above the win-back offer banner.
//
// ─── WHY THIS EXISTS ─────────────────────────────────────────────────────────
// The PDP root is `h-[calc(100dvh - var(--wb-offer-h,0px))]` and scrolls
// INTERNALLY. That subtraction is the only thing keeping an outer scrollbar off
// the page, so whatever sits above the PDP in flow must publish its real height.
//
// WinBackOfferBanner used to publish that number itself. It cannot keep doing so
// once a second bar exists, because it does not merely SET the property — it also
// REMOVES it, on three paths: the !isActive early branch, unmount, and every
// isActive flip (its effect is keyed on [isActive]). With two publishers, a
// win-back offer expiring mid-session would delete the whole stack's reservation
// while the coupon bar was still occupying space: every consumer falls back to
// 0px, the PDP root returns to a full 100dvh, and an outer scrollbar appears —
// exactly the bug the mechanism was built to prevent.
//
// So there is ONE publisher: this wrapper. The banner is now a plain child.
//
// ─── WHAT MOVED HERE FROM THE BANNER ─────────────────────────────────────────
//   * the publishing useEffect (deleted there)
//   * `sticky top-0 z-30` (deleted there)
//
// `sticky` on the WRAPPER, not on each bar, is also what makes stacking work at
// all: two independently `sticky top-0` children would both pin to offset zero
// and overlap on scroll. As one sticky unit with static children, the stack pins
// together.
//
// ─── MEASUREMENT ─────────────────────────────────────────────────────────────
// ResizeObserver, not a window-resize listener. The banner's mount+resize
// strategy was sufficient only because [isActive] was its effect key — it
// re-ran exactly when its own content appeared or disappeared. A wrapper with two
// independently-toggling children has no equivalent single key, so a
// resize-only listener would leave a stale (too tall) value behind when a child
// unmounts. The callback-ref + disconnect + `typeof ResizeObserver` guard shape
// follows the precedent at ProductDetailPageNew.jsx:180-183.
//
// Two properties carried over from the banner deliberately:
//   * the `if (h)` zero-guard — a measurement taken before layout would reserve
//     nothing and put us back to overlaying the header
//   * removing the property when the stack is empty, so every consumer falls
//     back to its `0px` and the layout is byte-identical to a build without this

import React, { useCallback, useRef } from "react";
import { useMatch } from "react-router-dom";
import WinBackOfferBanner from "./WinBackOfferBanner";
import CouponBar from "./Home/CouponBar";

export default function PromoStack() {
  const roRef = useRef(null);

  // ─── ROUTE GATE ────────────────────────────────────────────────────────────
  // This wrapper is mounted above MainLayout in App.js, so it renders on EVERY
  // route. That is correct for the win-back banner (app-wide by design) and wrong
  // for the coupon bar, which is store-home + PDP only.
  //
  // BOTH surfaces are now served from HERE — this is the single mount point for
  // the coupon bar. MyStore.jsx used to render its own copy, which put the bar
  // BELOW the header and trust strip on home while the PDP had it at the very
  // top. Rendering both from this wrapper, which sits above MainLayout and so
  // above the header, is what makes the position identical on the two pages.
  //
  // useMatch, not a pathname prefix: `/product-unavailable` is a real route and
  // `pathname.startsWith("/product")` would match it. This uses the router's own
  // matcher against the canonical PDP path (Routes.js:209) and the store home
  // (MainLayout.jsx:41). The legacy `/product-details/:name/:id` is a redirect
  // component, not the PDP, so it is deliberately not matched — it bounces to the
  // canonical path, which is.
  const isPdp = Boolean(useMatch("/product/:name/:id"));
  const isHome = Boolean(useMatch("/"));
  const showCoupon = isPdp || isHome;

  const stackRef = useCallback((node) => {
    const root = document.documentElement;

    if (roRef.current) {
      roRef.current.disconnect();
      roRef.current = null;
    }

    if (!node) {
      root.style.removeProperty("--wb-offer-h");
      return;
    }

    const publish = () => {
      const h = node.getBoundingClientRect().height;
      // Guard the 0 case two ways: a pre-layout measurement must not reserve
      // nothing, and a genuinely empty stack (no coupon, no offer) must REMOVE
      // the property rather than pin it at 0px, so consumers use their fallback.
      if (h) root.style.setProperty("--wb-offer-h", `${Math.round(h)}px`);
      else root.style.removeProperty("--wb-offer-h");
    };

    publish();

    if (typeof ResizeObserver !== "undefined") {
      roRef.current = new ResizeObserver(publish);
      roRef.current.observe(node);
    }
  }, []);
  // NOTE: the callback ref has an empty dep list on purpose — ResizeObserver is
  // what reacts to the coupon bar mounting/unmounting on a route change, so the
  // ref does not need to be re-created. Same reasoning as the precedent at
  // ProductDetailPageNew.jsx:180-183.

  return (
    <div ref={stackRef} className="sticky top-0 z-30">
      {showCoupon && <CouponBar />}
      <WinBackOfferBanner />
    </div>
  );
}
